import { createRef, useCallback, useEffect, useState } from "react";
import {
  IconNameEnum,
  useModuleBEM,
  useUiStyles,
} from "@b2bportal/core-themes";
import {
  CoreUiComponents,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { Root as SlotRoot } from "@radix-ui/react-slot";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./CarouselScroller.module.scss";
import {
  CarouselScrollerContextProvider,
  useCarouselScrollerContext,
} from "./context";

export type CarouselScrollerProps = PlatformComponentProps & {
  buttonClassName?: string;
  contentClassName?: string;
};

export const CarouselScroller = ({
  className,
  children,
  buttonClassName,
  contentClassName,
}: CarouselScrollerProps) => {
  return (
    <CarouselScrollerRoot className={className}>
      <CarouselScrollerContent contentClassName={contentClassName}>
        {children}
      </CarouselScrollerContent>
      <CarouselScrollerButton direction="left" className={buttonClassName}>
        <Icon iconName={IconNameEnum.backIcon} />
      </CarouselScrollerButton>
      <CarouselScrollerButton direction="right" className={buttonClassName}>
        <Icon iconName={IconNameEnum.actionIcon} />
      </CarouselScrollerButton>
    </CarouselScrollerRoot>
  );
};

const COMPONENT_KEY = CoreUiComponents.CarouselScroller;

export type CarouselScrollerRootProps = PlatformComponentProps;

export const CarouselScrollerRoot = (props: CarouselScrollerRootProps) => {
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const ref = createRef<HTMLDivElement>();

  const { children } = props;

  return (
    <CarouselScrollerContextProvider value={{ ref }}>
      <div className={cn("root")}>{children}</div>
    </CarouselScrollerContextProvider>
  );
};

type CarouselScrollerContentProps = PlatformComponentProps & {
  contentClassName?: string;
};

export const CarouselScrollerContent = (
  props: CarouselScrollerContentProps
) => {
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const context = useCarouselScrollerContext();

  const { className, children, contentClassName } = props;

  return (
    <div className={clsx(block, className)}>
      <div className={clsx(contentClassName, cn("content"))} ref={context.ref}>
        {children}
      </div>
    </div>
  );
};

interface CarouselScrollerButtonProps extends PlatformComponentProps {
  direction: "left" | "right";
  asChild?: boolean;
}

export const CarouselScrollerButton = (props: CarouselScrollerButtonProps) => {
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const context = useCarouselScrollerContext();
  const { className, children, direction, asChild = false } = props;

  const [isVisible, setIsVisible] = useState(true);

  const Component = asChild ? SlotRoot : "button";

  const scroll = useCallback(() => {
    context.ref.current?.scrollBy({
      left: direction === "left" ? -350 : 350,
      behavior: "smooth",
    });
  }, [direction, context.ref]);

  useEffect(() => {
    const element = context.ref.current;
    if (!element) return;

    const checkVisibility = () => {
      const { scrollLeft, scrollWidth, clientWidth } = element;
      const hasOverflow = scrollWidth > clientWidth;

      if (!hasOverflow) {
        setIsVisible(false);
        return;
      }

      if (direction === "left") {
        setIsVisible(scrollLeft > 0);
      } else {
        setIsVisible(scrollLeft < scrollWidth - clientWidth - 1);
      }
    };

    checkVisibility();

    element.addEventListener("scroll", checkVisibility);
    window.addEventListener("resize", checkVisibility);

    return () => {
      element.removeEventListener("scroll", checkVisibility);
      window.removeEventListener("resize", checkVisibility);
    };
  }, [context.ref, direction]);

  if (!isVisible) {
    return null;
  }

  return (
    <Component
      className={clsx(cn("button", [direction]), className)}
      onClick={scroll}
    >
      {children}
    </Component>
  );
};
